import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Order } from "@apis/index";
import { toast } from "react-toastify";
import Typography from "@components/typography";
import Button from "@components/button";
import useConfig from "@hooks/useConfig";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
  OverlayView,
} from "@react-google-maps/api";
import { Search } from "lucide-react"; // Assuming lucide-react is available, or use SVG

interface AssignDeliveryManModalProps {
  orderId: number | string;
  currentId?: number;
  hideModal: () => void;
  onSuccess?: (status: string) => void;
}

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "12px",
};

const AssignDeliveryManModal: React.FC<AssignDeliveryManModalProps> = ({
  orderId,
  currentId,
  hideModal,
  onSuccess,
}) => {
  const [deliveryMen, setDeliveryMen] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [storeLocation, setStoreLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { bucketUrl } = useConfig();

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.APP_MAP_API_KEY || "",
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    const fetchDeliveryMen = async () => {
      try {
        setLoading(true);
        const response: any = await Order.getNearbyDeliveryBoys(orderId);

        const deliveryList = Array.isArray(response)
          ? response
          : response?.data || response?.items || [];

        setDeliveryMen(deliveryList);

        // Store location from response
        if (response.storeLocation) {
          setStoreLocation({
            lat: response.storeLocation.latitude,
            lng: response.storeLocation.longitude,
          });
        }

        if (currentId) {
          const current = deliveryList.find((d: any) => d.id === currentId);
          if (current) setSelectedPerson(current);
        }
      } catch (error) {
        console.error("Failed to fetch delivery personnel:", error);
        toast.error("Failed to load delivery personnel");
      } finally {
        setLoading(false);
      }
    };
    fetchDeliveryMen();
  }, [orderId, currentId]);

  // Adjust map bounds when selection changes
  useEffect(() => {
    if (
      map &&
      storeLocation &&
      selectedPerson &&
      selectedPerson.currentLatitude &&
      selectedPerson.currentLongitude
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(storeLocation);
      bounds.extend({
        lat: Number(selectedPerson.currentLatitude),
        lng: Number(selectedPerson.currentLongitude),
      });
      map.fitBounds(bounds);

      // Add some padding
      const listener = google.maps.event.addListener(map, "idle", () => {
        if (map.getZoom()! > 16) map.setZoom(16);
        google.maps.event.removeListener(listener);
      });
    } else if (map && storeLocation) {
      map.panTo(storeLocation);
      map.setZoom(14);
    }
  }, [map, storeLocation, selectedPerson]);

  const handleAssign = async (deliveryBoyId: number) => {
    try {
      await Order.assignDelivery(orderId, deliveryBoyId);
      toast.success("Delivery man assigned successfully");
      if (onSuccess) onSuccess("Delivery Assigned");
      hideModal();
    } catch (e: any) {
      console.error("Failed to assign:", e);
      toast.error(e?.message || "Failed to assign delivery person");
    }
  };

  const filteredDeliveryMen = useMemo(() => {
    if (!searchTerm) return deliveryMen;
    const lower = searchTerm.toLowerCase();
    return deliveryMen.filter(
      (d) =>
        d.name?.toLowerCase().includes(lower) || d.phoneNo?.includes(lower),
    );
  }, [deliveryMen, searchTerm]);

  return (
    <div
      style={{
        padding: "24px",
        width: "95%",
        height: "80vh",
        maxHeight: "700px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div>
          <Typography styleText="font-size:20px;font-weight:700;color:#111827;">
            Assign Delivery Partner
          </Typography>
          <Typography styleText="font-size:13px;color:#6B7280;margin-top:4px">
            Select an available delivery partner for this order.
          </Typography>
        </div>
      </div>

      {/* Content Area */}
      <div
        style={{
          display: "flex",
          gap: "24px",
          flex: 1,
          overflow: "hidden", // Prevent outer scroll
        }}
      >
        {/* Left Column: Search & List */}
        <div
          style={{
            flex: "1",
            minWidth: "350px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Search Bar */}
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "86%",
                padding: "10px 12px 10px 36px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                fontSize: "14px",
                outline: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
              }}
            >
              <Search size={16} />
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div style={{ padding: "40px", textAlign: "center", flex: 1 }}>
              <div
                className="spinner"
                style={{
                  width: "24px",
                  height: "24px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 12px",
                }}
              ></div>
              <Typography styleText="color:#6B7280;font-size:14px">
                Finding partners...
              </Typography>
            </div>
          ) : filteredDeliveryMen.length === 0 ? (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                backgroundColor: "#f9fafb",
                borderRadius: "12px",
                border: "1px dashed #e5e7eb",
                flex: 1,
              }}
            >
              <Typography styleText="font-size:16px;color:#374151">
                No active delivery partners found
              </Typography>
              <Typography styleText="font-size:13px;color:#9CA3AF;margin-top:4px">
                {searchTerm
                  ? "Try a different search term."
                  : "Try again later."}
              </Typography>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                overflowY: "auto",
                paddingRight: "4px",
                flex: 2,
              }}
            >
              {filteredDeliveryMen.map((person) => {
                const isSelected = selectedPerson?.id === person.id;
                const initials = person.name?.charAt(0).toUpperCase() || "D";
                const activeOrders = person.activeOrdersCount || 0;
                const isAvailable = person.isAvailable;

                return (
                  <div
                    key={person.id}
                    onClick={() => setSelectedPerson(person)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: isSelected
                        ? "1px solid #3b82f6"
                        : "1px solid #e5e7eb",
                      backgroundColor: isSelected ? "#eff6ff" : "white",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {/* Avatar */}
                    <div style={{ marginRight: "12px", position: "relative" }}>
                      {person.avatar ? (
                        <img
                          src={`${bucketUrl}/${person.avatar}`}
                          alt={person.name}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid #e5e7eb",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "#e0e7ff",
                            color: "#4338ca",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          {initials}
                        </div>
                      )}
                      {/* Status Dot */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          right: "0",
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: isAvailable ? "#10B981" : "#D1D5DB",
                          border: "1px solid white",
                        }}
                      ></div>
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Typography styleText="font-size:14px;font-weight:600;color:#1f2937">
                          {person.name}
                        </Typography>
                        {activeOrders > 0 && (
                          <span
                            style={{
                              fontSize: "10px",
                              padding: "2px 6px",
                              borderRadius: "10px",
                              backgroundColor: "#f3f4f6",
                              color: "#4b5563",
                              fontWeight: "500",
                            }}
                          >
                            {activeOrders} active
                          </span>
                        )}
                      </div>
                      <Typography styleText="font-size:12px;color:#6b7280;margin-top:2px">
                        {person.phoneNo}
                      </Typography>
                    </div>

                    {/* Selection Indicator */}
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border: isSelected
                          ? "5px solid #3b82f6"
                          : "2px solid #d1d5db",
                        backgroundColor: "white",
                        transition: "all 0.15s",
                      }}
                    ></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Map */}
        <div
          style={{
            flex: "2",
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            position: "relative",
          }}
        >
          {isLoaded && storeLocation ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={storeLocation}
              zoom={14}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
              }}
            >
              {/* Store Marker */}
              <Marker
                position={storeLocation}
                icon={{
                  url: "https://cdn-icons-png.flaticon.com/512/3514/3514491.png", // Shop Icon
                  scaledSize: new window.google.maps.Size(32, 32),
                }}
              />

              {/* Selected Delivery Boy Marker with Heartbeat */}
              {selectedPerson &&
                selectedPerson.currentLatitude &&
                selectedPerson.currentLongitude && (
                  <OverlayView
                    position={{
                      lat: Number(selectedPerson.currentLatitude),
                      lng: Number(selectedPerson.currentLongitude),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div style={{ position: "relative" }}>
                      {/* Heartbeat Rings */}
                      <div className="heartbeat-ring"></div>
                      <div className="heartbeat-ring delay-1"></div>

                      {/* Delivery Icon */}
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          background: "#fff",
                          border: "2px solid #3b82f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          position: "relative",
                          zIndex: 2,
                          transform: "translate(-50%, -50%)",
                        }}
                      >
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/3063/3063823.png"
                          alt="Delivery"
                          style={{ width: "24px", height: "24px" }}
                        />
                      </div>
                    </div>
                  </OverlayView>
                )}
            </GoogleMap>
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f3f4f6",
              }}
            >
              <Typography styleText="color:#9ca3af">Map Loading...</Typography>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          paddingTop: "16px",
          borderTop: "1px solid #f3f4f6",
          flexShrink: 0,
        }}
      >
        <Button title="Cancel" variant="outline" onClick={hideModal} />
        <Button
          title="Assign Partner"
          disabled={!selectedPerson || loading}
          onClick={() => selectedPerson && handleAssign(selectedPerson.id)}
        />
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .heartbeat-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: rgba(59, 130, 246, 0.6);
          animation: pulse 2s infinite cubic-bezier(0.4, 0, 0.6, 1);
          z-index: 1;
        }

        .delay-1 {
          animation-delay: 0.5s;
        }

        @keyframes pulse {
          0% {
            width: 20px;
            height: 20px;
            opacity: 0.8;
          }
          100% {
            width: 100px;
            height: 100px;
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AssignDeliveryManModal;
